import type { AdapterConfigField, AdapterKind, AdapterMeta, EventTypeMeta } from '../types';

// ---------------------------------------------------------------------------
// Client-side validators — fast feedback only. Backend remains source of truth.
// ---------------------------------------------------------------------------

const isNonEmptyString = (v: unknown): v is string => typeof v === 'string' && v.trim().length > 0;

const required =
  (key: string) =>
  (value: unknown): string | null =>
    isNonEmptyString(value) ? null : `integrations.errors.required:${key}`;

/**
 * Fetch a parseable URL or null if invalid. We avoid throwing into Mantine's
 * validate callback so the message stays user-friendly.
 */
const tryUrl = (value: unknown): URL | null => {
  if (!isNonEmptyString(value)) return null;
  try {
    return new URL(value);
  } catch {
    return null;
  }
};

const urlValidator = (value: unknown): string | null => {
  if (!isNonEmptyString(value)) return 'integrations.errors.urlRequired';
  return tryUrl(value) ? null : 'integrations.errors.urlInvalid';
};

/**
 * Mirrors backend `ssrf_guard` (rough — the backend does the authoritative
 * check including DNS resolution). We only block obvious localhost / private
 * literals here so the UI doesn't gate on a full network library.
 */
const ssrfGuard = (value: unknown): string | null => {
  const url = tryUrl(value);
  if (!url) return urlValidator(value);
  if (url.protocol !== 'http:' && url.protocol !== 'https:') {
    return 'integrations.errors.urlScheme';
  }
  const host = url.hostname.toLowerCase();
  const blocked = [
    'localhost',
    '127.0.0.1',
    '0.0.0.0',
    '::1',
    '169.254.169.254', // AWS / GCP metadata service
  ];
  if (blocked.includes(host)) return 'integrations.errors.urlPrivate';
  if (host.endsWith('.local') || host.endsWith('.internal')) {
    return 'integrations.errors.urlPrivate';
  }
  // 10/8, 172.16/12, 192.168/16
  if (/^10\./.test(host)) return 'integrations.errors.urlPrivate';
  if (/^192\.168\./.test(host)) return 'integrations.errors.urlPrivate';
  if (/^172\.(1[6-9]|2\d|3[01])\./.test(host)) return 'integrations.errors.urlPrivate';
  return null;
};

const slackHostValidator = (value: unknown): string | null => {
  const generic = urlValidator(value);
  if (generic) return generic;
  const url = tryUrl(value)!;
  return url.host === 'hooks.slack.com' ? null : 'integrations.errors.slackHost';
};

const discordHostValidator = (value: unknown): string | null => {
  const generic = urlValidator(value);
  if (generic) return generic;
  const url = tryUrl(value)!;
  const okHost = url.host === 'discord.com' || url.host === 'discordapp.com';
  if (!okHost) return 'integrations.errors.discordHost';
  if (!url.pathname.startsWith('/api/webhooks/')) {
    return 'integrations.errors.discordPath';
  }
  return null;
};

const googleHostValidator = (value: unknown): string | null => {
  const generic = ssrfGuard(value);
  if (generic) return generic;
  const url = tryUrl(value)!;
  const ok = url.host.endsWith('google.com') || url.host.endsWith('googleusercontent.com');
  return ok ? null : 'integrations.errors.googleHost';
};

const emailListValidator = (value: unknown): string | null => {
  const list = Array.isArray(value) ? value : [];
  if (list.length === 0) return 'integrations.errors.emailListEmpty';
  for (const addr of list) {
    if (typeof addr !== 'string' || !addr.includes('@')) {
      return 'integrations.errors.emailInvalid';
    }
  }
  return null;
};

const pipedriveDomainValidator = (value: unknown): string | null => {
  if (!isNonEmptyString(value)) return 'integrations.errors.required:companyDomain';
  const re = /^[a-zA-Z0-9][a-zA-Z0-9-]*[a-zA-Z0-9]\.pipedrive\.com$/;
  return re.test(value) ? null : 'integrations.errors.pipedriveDomain';
};

const mailchimpStatusValidator = (value: unknown): string | null => {
  if (value === 'subscribed' || value === 'pending') return null;
  return 'integrations.errors.mailchimpStatus';
};

const jsonObjectValidator = (value: unknown): string | null => {
  if (value === undefined || value === null || value === '') return null;
  if (typeof value === 'object') return null;
  if (typeof value !== 'string') return 'integrations.errors.jsonInvalid';
  try {
    const parsed = JSON.parse(value);
    if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) return null;
    return 'integrations.errors.jsonObjectExpected';
  } catch {
    return 'integrations.errors.jsonInvalid';
  }
};

// ---------------------------------------------------------------------------
// Adapter metadata
// ---------------------------------------------------------------------------

const URL_FIELD = (key: string, labelKey: string, validator = ssrfGuard): AdapterConfigField => ({
  key,
  label: labelKey,
  type: 'url',
  required: true,
  placeholder: 'https://...',
  validate: validator,
});

const genericWebhook: AdapterMeta = {
  kind: 'generic_webhook',
  label: 'integrations.adapters.generic_webhook.label',
  description: 'integrations.adapters.generic_webhook.description',
  icon: 'webhook',
  capabilities: { retry: true, hmac: true, replayable: true, idempotency: true },
  configFields: [
    URL_FIELD('url', 'integrations.fields.url'),
    {
      key: 'custom_headers',
      label: 'integrations.fields.customHeaders',
      helperText: 'integrations.fields.customHeadersHelp',
      type: 'json',
      required: false,
      placeholder: '{"X-Tenant": "acme"}',
      validate: jsonObjectValidator,
    },
  ],
  secretsFields: [],
};

const zapierWebhook: AdapterMeta = {
  kind: 'zapier_webhook',
  label: 'integrations.adapters.zapier_webhook.label',
  description: 'integrations.adapters.zapier_webhook.description',
  icon: 'bolt',
  capabilities: { retry: true, hmac: true, replayable: true, idempotency: true },
  configFields: [URL_FIELD('url', 'integrations.fields.zapierUrl')],
  secretsFields: [],
};

const slackUrl: AdapterMeta = {
  kind: 'slack_url',
  label: 'integrations.adapters.slack_url.label',
  description: 'integrations.adapters.slack_url.description',
  icon: 'brand-slack',
  capabilities: { retry: true, hmac: false, replayable: true, idempotency: false },
  configFields: [
    {
      key: 'webhook_url',
      label: 'integrations.fields.slackWebhookUrl',
      helperText: 'integrations.fields.slackWebhookUrlHelp',
      type: 'url',
      required: true,
      placeholder: 'https://hooks.slack.com/services/...',
      validate: slackHostValidator,
    },
  ],
  secretsFields: [],
};

const discordUrl: AdapterMeta = {
  kind: 'discord_url',
  label: 'integrations.adapters.discord_url.label',
  description: 'integrations.adapters.discord_url.description',
  icon: 'brand-discord',
  capabilities: { retry: true, hmac: false, replayable: true, idempotency: false },
  configFields: [
    {
      key: 'webhook_url',
      label: 'integrations.fields.discordWebhookUrl',
      helperText: 'integrations.fields.discordWebhookUrlHelp',
      type: 'url',
      required: true,
      placeholder: 'https://discord.com/api/webhooks/...',
      validate: discordHostValidator,
    },
    {
      key: 'username',
      label: 'integrations.fields.discordUsername',
      helperText: 'integrations.fields.discordUsernameHelp',
      type: 'text',
      required: false,
    },
  ],
  secretsFields: [],
};

const googleSheets: AdapterMeta = {
  kind: 'google_sheets_webhook',
  label: 'integrations.adapters.google_sheets_webhook.label',
  description: 'integrations.adapters.google_sheets_webhook.description',
  icon: 'table',
  capabilities: { retry: true, hmac: false, replayable: true, idempotency: false },
  configFields: [
    {
      key: 'webhook_url',
      label: 'integrations.fields.sheetsWebhookUrl',
      helperText: 'integrations.fields.sheetsWebhookUrlHelp',
      type: 'url',
      required: true,
      placeholder: 'https://script.google.com/macros/s/.../exec',
      validate: googleHostValidator,
    },
    {
      key: 'sheet_name',
      label: 'integrations.fields.sheetName',
      helperText: 'integrations.fields.sheetNameHelp',
      type: 'text',
      required: false,
    },
  ],
  secretsFields: [],
};

const email: AdapterMeta = {
  kind: 'email',
  label: 'integrations.adapters.email.label',
  description: 'integrations.adapters.email.description',
  icon: 'mail',
  capabilities: { retry: false, hmac: false, replayable: false, idempotency: false },
  configFields: [
    {
      key: 'to',
      label: 'integrations.fields.emailTo',
      helperText: 'integrations.fields.emailToHelp',
      type: 'email-list',
      required: true,
      validate: emailListValidator,
    },
    {
      key: 'subject_template',
      label: 'integrations.fields.subjectTemplate',
      helperText: 'integrations.fields.subjectTemplateHelp',
      type: 'text',
      required: true,
      validate: required('subjectTemplate'),
    },
    {
      key: 'body_template',
      label: 'integrations.fields.bodyTemplate',
      helperText: 'integrations.fields.bodyTemplateHelp',
      type: 'text',
      required: false,
    },
  ],
  secretsFields: [],
};

const formshiveEndpoint: AdapterMeta = {
  kind: 'formshive_endpoint',
  label: 'integrations.adapters.formshive_endpoint.label',
  description: 'integrations.adapters.formshive_endpoint.description',
  icon: 'forms',
  capabilities: { retry: true, hmac: true, replayable: true, idempotency: true },
  configFields: [
    {
      key: 'form_id',
      label: 'integrations.fields.formId',
      helperText: 'integrations.fields.formIdHelp',
      type: 'text',
      required: true,
      validate: required('formId'),
    },
    {
      key: 'api_url_override',
      label: 'integrations.fields.apiUrlOverride',
      helperText: 'integrations.fields.apiUrlOverrideHelp',
      type: 'url',
      required: false,
      validate: (v) => (isNonEmptyString(v) ? ssrfGuard(v) : null),
    },
  ],
  secretsFields: [
    {
      key: 'submit_token',
      label: 'integrations.fields.submitToken',
      helperText: 'integrations.fields.submitTokenHelp',
      type: 'password',
      required: false,
    },
  ],
};

// --- Formshive-only vendor adapters ----------------------------------------

const pipedrive: AdapterMeta = {
  kind: 'api_pipedrive',
  label: 'integrations.adapters.api_pipedrive.label',
  description: 'integrations.adapters.api_pipedrive.description',
  icon: 'building-store',
  capabilities: { retry: true, hmac: false, replayable: true, idempotency: false },
  configFields: [
    {
      key: 'company_domain',
      label: 'integrations.fields.pipedriveDomain',
      helperText: 'integrations.fields.pipedriveDomainHelp',
      type: 'text',
      required: true,
      placeholder: 'mycompany.pipedrive.com',
      validate: pipedriveDomainValidator,
    },
    {
      key: 'create_person',
      label: 'integrations.fields.createPerson',
      type: 'checkbox',
      required: false,
    },
    {
      key: 'create_lead',
      label: 'integrations.fields.createLead',
      type: 'checkbox',
      required: false,
    },
    {
      key: 'default_owner_id',
      label: 'integrations.fields.defaultOwnerId',
      type: 'number',
      required: false,
    },
    {
      key: 'default_org_id',
      label: 'integrations.fields.defaultOrgId',
      type: 'number',
      required: false,
    },
    {
      key: 'lead_title_template',
      label: 'integrations.fields.leadTitleTemplate',
      helperText: 'integrations.fields.leadTitleTemplateHelp',
      type: 'text',
      required: false,
    },
    {
      key: 'custom_field_mapping',
      label: 'integrations.fields.customFieldMapping',
      helperText: 'integrations.fields.customFieldMappingHelp',
      type: 'json',
      required: false,
      placeholder: '{"email": "primary_email"}',
      validate: jsonObjectValidator,
    },
  ],
  secretsFields: [
    {
      key: 'api_key',
      label: 'integrations.fields.apiKey',
      helperText: 'integrations.fields.pipedriveApiKeyHelp',
      type: 'password',
      required: true,
      validate: required('apiKey'),
    },
  ],
};

const mailchimp: AdapterMeta = {
  kind: 'api_mailchimp',
  label: 'integrations.adapters.api_mailchimp.label',
  description: 'integrations.adapters.api_mailchimp.description',
  // Tabler doesn't ship a Mailchimp brand glyph; the generic mail icon is the
  // closest match without pulling in another icon set.
  icon: 'mail',
  capabilities: { retry: true, hmac: false, replayable: true, idempotency: false },
  configFields: [
    {
      key: 'list_id',
      label: 'integrations.fields.mailchimpListId',
      helperText: 'integrations.fields.mailchimpListIdHelp',
      type: 'text',
      required: true,
      validate: required('listId'),
    },
    {
      key: 'status',
      label: 'integrations.fields.mailchimpStatus',
      type: 'select',
      required: true,
      options: [
        { value: 'subscribed', label: 'integrations.fields.mailchimpStatusSubscribed' },
        { value: 'pending', label: 'integrations.fields.mailchimpStatusPending' },
      ],
      validate: mailchimpStatusValidator,
    },
    {
      key: 'double_optin',
      label: 'integrations.fields.doubleOptin',
      type: 'checkbox',
      required: false,
    },
    {
      key: 'merge_field_mapping',
      label: 'integrations.fields.mergeFieldMapping',
      helperText: 'integrations.fields.mergeFieldMappingHelp',
      type: 'json',
      required: false,
      placeholder: '{"name": "FNAME"}',
      validate: jsonObjectValidator,
    },
    {
      key: 'default_tags',
      label: 'integrations.fields.defaultTags',
      helperText: 'integrations.fields.defaultTagsHelp',
      type: 'array-string',
      required: false,
    },
  ],
  secretsFields: [
    {
      key: 'api_key',
      label: 'integrations.fields.apiKey',
      helperText: 'integrations.fields.mailchimpApiKeyHelp',
      type: 'password',
      required: true,
      validate: required('apiKey'),
    },
  ],
};

const kit: AdapterMeta = {
  kind: 'api_kit',
  label: 'integrations.adapters.api_kit.label',
  description: 'integrations.adapters.api_kit.description',
  icon: 'mail-forward',
  capabilities: { retry: true, hmac: false, replayable: true, idempotency: false },
  configFields: [
    {
      key: 'form_id',
      label: 'integrations.fields.kitFormId',
      helperText: 'integrations.fields.kitFormIdHelp',
      type: 'text',
      required: false,
    },
    {
      key: 'field_mapping',
      label: 'integrations.fields.fieldMapping',
      helperText: 'integrations.fields.fieldMappingHelp',
      type: 'json',
      required: false,
      placeholder: '{"email": "email_address"}',
      validate: jsonObjectValidator,
    },
    {
      key: 'default_tags',
      label: 'integrations.fields.defaultTags',
      helperText: 'integrations.fields.defaultTagsHelp',
      type: 'array-string',
      required: false,
    },
  ],
  secretsFields: [
    {
      key: 'api_key',
      label: 'integrations.fields.apiKey',
      helperText: 'integrations.fields.kitApiKeyHelp',
      type: 'password',
      required: true,
      validate: required('apiKey'),
    },
  ],
};

const ADAPTER_REGISTRY: Record<AdapterKind, AdapterMeta> = {
  generic_webhook: genericWebhook,
  zapier_webhook: zapierWebhook,
  slack_url: slackUrl,
  discord_url: discordUrl,
  google_sheets_webhook: googleSheets,
  email,
  formshive_endpoint: formshiveEndpoint,
  api_pipedrive: pipedrive,
  api_mailchimp: mailchimp,
  api_kit: kit,
};

export function getAdapterMeta(kind: AdapterKind | string): AdapterMeta | undefined {
  return (ADAPTER_REGISTRY as Record<string, AdapterMeta>)[kind];
}

export function listAdapters(): AdapterMeta[] {
  return Object.values(ADAPTER_REGISTRY);
}

// ---------------------------------------------------------------------------
// Event-type registry
// ---------------------------------------------------------------------------

export const FORMSHIVE_EVENT_TYPES: EventTypeMeta[] = [
  {
    type: 'message.received',
    label: 'integrations.events.message_received.label',
    description: 'integrations.events.message_received.description',
  },
];
